import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import { updateAccount } from '../account/actions/update-account';
import { updateSubscription } from '../account/components/subscription-v3/actions/update-subscription';
import { ItemizedPlanSelectorBody } from '../account/components/subscription-v3/itemized-plan-selector-body';
import { loadIndustries } from '../action/load-industries';
import { addOrganization } from '../action/org/add-organization';
import { createVCard } from '../action/org/create-v-card';
import { updateOrg } from '../action/org/update-org';
import { crawlOrganizationWebsite } from '../organization-scraped-image/actions/crawl-organization-website';
import { userSelector } from '../reducers/current-user-selectors';
import { industriesSelector } from '../reducers/industry-selectors';

import * as FileUploadApi from '../api/file-upload-api-client';
import { validateAreaCode } from '../twilio/api/twilio-api';
import { UiAreaCodeTextField } from '../ui/ui-area-code-text-field';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';
import { UiPhoneTextField } from '../ui/ui-phone-text-field';

import { AddAccountExecutive } from './admin-panel/admin-account-widgets/add-account-executive';
import * as Admin from './api/admin-api';
import { UserForm } from './user-form';

import { generateOrgKey } from './util/generate-org-key';
import { convertPhoneNumberToE164 } from '../util/formatters';
import { JOB_TEMPLATE_OPTIONS } from '../util/job-template-options';
import { isValueANumber } from '../util/number-utils';
import { SORT_ORDER, sortAlphabetically } from '../util/sort-functions';
import { isPhoneNumber, isValidHttpUrl, isZipCode } from '../util/string-validators';
import { SubscriptionCadence } from '../util/subscription-cadence';
import { CustomerImageEditor } from '../widgets/customer-image-editor';
import { ImageGallery } from '../widgets/image-gallery';
import { StateAutocomplete } from '../widgets/state-autocomplete';
import { UsersPicker } from '../widgets/users-picker';

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    addOrganization: (orgInfo: OrgCreateRequest) => dispatch(addOrganization(orgInfo)),
    updateOrg: (orgId: number, update: Partial<Org>) => dispatch(updateOrg(orgId, update)),
    loadIndustries: () => dispatch(loadIndustries()),
    crawlOrganizationWebsite: (organizationWebsiteURL: string) =>
      dispatch(crawlOrganizationWebsite(organizationWebsiteURL)),
    updateAccount: (accountId: number, account: TeamEngineAccountUpdateRequest) =>
      dispatch(updateAccount(accountId, account)),
    createVCard: (orgId: number) => dispatch(createVCard(orgId)),
    updateSubscription: (accountId: number, subscriptionUpdate: SubscriptionUpdateV3) =>
      dispatch(updateSubscription(accountId, subscriptionUpdate)),
  },
});

const mapStateToProps = (state: any) => ({
  industries: industriesSelector(state),
  currentUser: userSelector(state),
});

type InputProps = {
  accountId?: number | null;
  onAdd: (org: Org) => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps & StateProps;

type State = {
  org: {
    name: string;
    website: string;
    logo_path: string;
    industryId: string | number;
    org_key: string;
    city: string;
    state: string | null;
    jobTemplate: string;
    indeedEmail: string;
    indeedZipCode: string;
    internal: boolean;
  };
  account: {
    businessStreet: string;
    businessUnit: string;
    businessCity: string;
    businessState: string | null;
    businessZipCode: string;
    subscriptionCadence: SubscriptionCadence | null;
    baseMonthlyRateStripePlanId: string | null;
    isPartnerReferral: boolean;
    businessPhoneNumber: string | null;
    accountExecutive: string | null;
    stripePriceIds: {
      activeJobQuantity?: number;
      activeEmployeeQuantity?: number;
      additionalOrgQuantity?: number;
    };
  };
  file: Blob | File | null;
  fileExtension: string | null | undefined;
  adding: boolean;
  addingUser: boolean;
  createdOrg: Org | null;
  isCreating: boolean;
  homePageImages: Array<string> | null;
  isLoadingImages: boolean;
  selectedImageUrl: string | null;
  hasAccountPhoneNumberError: boolean;
  usersToGrantAccess: User[];
  areaCode: string;
  isAreaCodeValid: boolean | null;
};

const MIN_IMAGE_SIZE = 120; // px
const defaultState = {
  org: {
    name: '',
    website: '',
    logo_path: '',
    industryId: '',
    org_key: '',
    city: '',
    state: null,
    jobTemplate: '',
    indeedEmail: '',
    indeedZipCode: '',
    internal: false,
  },
  account: {
    businessStreet: '',
    businessUnit: '',
    businessCity: '',
    businessState: null,
    businessZipCode: '',
    subscriptionCadence: SubscriptionCadence.MONTHLY,
    baseMonthlyRateStripePlanId: null,
    isPartnerReferral: false,
    businessPhoneNumber: '',
    accountExecutive: null,
    stripePriceIds: {},
  },
  file: null,
  fileExtension: null,
  adding: false,
  addingUser: false,
  createdOrg: null,
  isCreating: false,
  homePageImages: null,
  isLoadingImages: false,
  selectedImageUrl: null,
  hasAccountPhoneNumberError: false,
  usersToGrantAccess: [],
  areaCode: '',
  isAreaCodeValid: null,
};
class AddOrganizationOrAccountComponent extends React.PureComponent<Props, State> {
  state: State = defaultState;

  componentDidMount() {
    const { industries, actions } = this.props;
    if (!industries) {
      actions.loadIndustries();
    }
  }

  onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    this.setState((prevState) => ({
      org: {
        ...prevState.org,
        name,
        org_key: generateOrgKey(name),
      },
    }));
  };

  handleChange = (name: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
    let v = event.target.value;
    this.setState((prevState) => {
      let accountCity = prevState.account.businessCity;
      if (
        name === 'city' &&
        (!prevState.account.businessCity || prevState.account.businessCity === prevState.org.city)
      ) {
        accountCity = v as string;
      }
      if (name === 'indeedZipCode') {
        v = (v as string).replace(/[^\d-]/g, '').substr(0, 10);
      }
      return {
        org: {
          ...prevState.org,
          [name]: v,
        },
        account: {
          ...prevState.account,
          businessCity: accountCity,
        },
      };
    });
  };

  handleStateChange = (state: string | null) => {
    this.setState((prevState) => {
      let accountState = prevState.account.businessState;
      if (
        !prevState.account.businessState ||
        prevState.account.businessState === prevState.org.state
      ) {
        accountState = state;
      }
      return {
        org: {
          ...prevState.org,
          state,
        },
        account: {
          ...prevState.account,
          businessState: accountState,
        },
      };
    });
  };

  handleInternalChange = () => {
    this.setState((prevState) => ({
      org: {
        ...prevState.org,
        internal: !prevState.org.internal,
      },
    }));
  };

  handleAccountChange = (name: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
    const v = event.target.value;
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        [name]: v,
      },
    }));
  };

  handleAccountPhoneNumberChange = (businessPhoneNumber: string | null) => {
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        businessPhoneNumber: businessPhoneNumber || '',
      },
    }));
  };

  handleAccountPhoneNumberError = (error: string | null) => {
    this.setState({
      hasAccountPhoneNumberError: Boolean(error),
    });
  };

  handleAreaCodeChange = (areaCode: string | null) => {
    this.setState({
      areaCode: areaCode || '',
    });
    if (areaCode && areaCode.length === 3) {
      validateAreaCode(areaCode).then((responseValue) =>
        this.setState({
          isAreaCodeValid: responseValue,
        }),
      );
    } else {
      this.setState({ isAreaCodeValid: areaCode && areaCode.length > 3 ? false : null });
    }
  };

  handleChangePriceQuantity = (
    priceQuantity: number | null,
    field: 'activeJobQuantity' | 'activeEmployeeQuantity' | 'additionalOrgQuantity',
  ) => {
    const newPriceQuantity = priceQuantity === null ? undefined : priceQuantity;
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        stripePriceIds: {
          ...(prevState.account.stripePriceIds || {}),
          [field]: newPriceQuantity,
        },
      },
    }));
  };

  handleChangeSubscriptionCadence = (subscriptionCadence: SubscriptionCadence | null) => {
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        subscriptionCadence,
      },
    }));
  };

  handleChangeJobCount = (newJobCount: string | null) => {
    const jobCount = isValueANumber(newJobCount) ? parseInt(newJobCount, 10) : null;
    this.handleChangePriceQuantity(jobCount, 'activeJobQuantity');
  };

  handleChangeEmployeeCount = (newEmployeeCount: string | null) => {
    const empCount = isValueANumber(newEmployeeCount) ? parseInt(newEmployeeCount, 10) : null;
    this.handleChangePriceQuantity(empCount, 'activeEmployeeQuantity');
  };

  handleChangeAdditionalOrgCount = (newOrgCount: string | null) => {
    const orgCount = isValueANumber(newOrgCount) ? parseInt(newOrgCount, 10) : null;
    this.handleChangePriceQuantity(orgCount, 'additionalOrgQuantity');
  };

  handleIsPartnerReferralChange = async () => {
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        isPartnerReferral: !prevState.account.isPartnerReferral,
      },
    }));
  };

  handleAccountStateChange = (businessState: string | null) => {
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        businessState,
      },
    }));
  };

  handleAccountExecutiveChange = (accountExecutive: string | null) => {
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        accountExecutive,
      },
    }));
  };

  handleGrantAccessChange = (usersToGrantAccess: User[]) => {
    this.setState({ usersToGrantAccess });
  };

  fileChange = async (fileChangeObject: {
    file: Blob | null;
    fullResFile: any;
    fileExtension: string | null;
  }) => {
    this.setState({
      file: fileChangeObject.file,
      fileExtension: fileChangeObject.fileExtension,
    });
  };

  addOrg = async () => {
    const { accountId, actions } = this.props;
    const {
      file,
      fileExtension,
      selectedImageUrl,
      org: stateOrg,
      account,
      usersToGrantAccess,
      areaCode,
    } = this.state;

    if (!selectedImageUrl && (!file || !fileExtension)) {
      return null;
    }

    this.setState({ isCreating: true });

    const initialUsernames = accountId ? usersToGrantAccess.map((u) => u.username) : [];

    // First, create the org
    const orgInfo = {
      ...stateOrg,
      indeedEmail: stateOrg.indeedEmail ? stateOrg.indeedEmail : null,
      initialUsernames,
      areaCode,
      ...(accountId ? { accountId } : {}),
      ...(selectedImageUrl ? { logoImageUrl: selectedImageUrl } : {}),
      ...(account
        ? {
            ...(account.subscriptionCadence
              ? { subscriptionCadence: account.subscriptionCadence }
              : {}),
            ...(account.baseMonthlyRateStripePlanId
              ? { baseMonthlyRateStripePlanId: account.baseMonthlyRateStripePlanId }
              : {}),
            ...(account.isPartnerReferral !== null
              ? { isPartnerReferral: account.isPartnerReferral }
              : {}),
          }
        : {}),
    };

    const createdOrg = await actions.addOrganization(orgInfo);
    // If this request fails, the object that is returned here will not have an accountId, so we should not try and updated the undefined value
    if (!createdOrg.accountId) {
      return null;
    }
    if (!accountId) {
      account.accountExecutive = account.accountExecutive || null;
      account.businessPhoneNumber = isPhoneNumber(account.businessPhoneNumber)
        ? convertPhoneNumberToE164(account.businessPhoneNumber)
        : null;
      actions.updateAccount(createdOrg.accountId, account);

      if (account.stripePriceIds) {
        const subscriptionUpdate: SubscriptionUpdateV3 = {
          subscriptionCadence: account.subscriptionCadence || SubscriptionCadence.MONTHLY,
          activeJobQuantity: account.stripePriceIds.activeJobQuantity || 0,
          activeEmployeeQuantity: account.stripePriceIds.activeEmployeeQuantity || 0,
          additionalOrgQuantity: account.stripePriceIds.additionalOrgQuantity || 0,
        };
        actions.updateSubscription(createdOrg.accountId, subscriptionUpdate);
      }
    }

    if (file && fileExtension) {
      // Then use the org_key to upload the image. The issue is that if we found
      // a conflict in the database on the org_key, it might have had a number
      // appended to the end of it
      const orgKey = createdOrg.org_key;
      const fileName = `${orgKey}_logo${new Date().getTime()}.${fileExtension}`;
      const policyResult = await Admin.getNewOrgLogoPolicy(orgKey);
      const params = await policyResult.json();

      await FileUploadApi.uploadFile(
        params.fileUploadPolicy,
        params.orgPolicy.bucket,
        `customer/${orgKey}/${fileName}`,
        file,
      );
      await this.props.actions.updateOrg(createdOrg.org_id, { logo_path: fileName });
    }

    if (createdOrg.phoneNumber) {
      await this.props.actions.createVCard(createdOrg.org_id);
    }

    this.setState({ isCreating: false });
    return createdOrg;
  };

  closeAddDialog = () => {
    this.setState({ adding: false });
  };

  openAddDialog = () => {
    this.setState({
      ...defaultState,
      adding: true,
    });
  };

  handleCloseUserDialog = () => {
    const { createdOrg } = this.state;
    this.setState({ addingUser: false });
    if (createdOrg) {
      this.props.onAdd(createdOrg);
    }
  };

  handleAddClick = async () => {
    const createdOrg = await this.addOrg();
    if (createdOrg) {
      this.props.onAdd(createdOrg);
    }
  };

  handleAddWithUserClick = async () => {
    const createdOrg = await this.addOrg();
    if (createdOrg) {
      this.setState({
        createdOrg,
        addingUser: true,
        adding: false,
      });
    }
  };

  loadImages = async () => {
    const { org } = this.state;
    this.setState(
      {
        isLoadingImages: true,
      },
      async () => {
        const homePageImages = await this.props.actions.crawlOrganizationWebsite(org.website);
        this.setState({
          homePageImages,
          isLoadingImages: false,
        });
      },
    );
  };

  selectImage = (image: ImageGalleryImage | null) => {
    this.setState({
      selectedImageUrl: image?.imageUrl ?? null,
    });
  };

  handleClearImage = () => this.selectImage(null);

  renderDialog() {
    const {
      org,
      file,
      isCreating,
      homePageImages,
      selectedImageUrl,
      isLoadingImages,
      account,
      hasAccountPhoneNumberError,
      areaCode,
      isAreaCodeValid,
    } = this.state;
    const { industries, accountId } = this.props;

    const isAddAccount = !accountId;
    const entityName = isAddAccount ? 'Account' : 'Organization';

    const submitDisabled =
      org.industryId === '' ||
      org.org_key.length === 0 ||
      org.name.length === 0 ||
      org.website.length === 0 ||
      !isValidHttpUrl(org.website) ||
      org.city.length === 0 ||
      org.industryId === '' ||
      !org.state ||
      (file === null && !selectedImageUrl) ||
      (this.state.areaCode && !this.state.isAreaCodeValid) ||
      (isAddAccount
        ? !(
            account.businessStreet &&
            account.businessCity &&
            account.businessState &&
            account.businessZipCode &&
            isZipCode(account.businessZipCode) &&
            account.businessPhoneNumber &&
            !hasAccountPhoneNumberError
          )
        : false);

    const { accountExecutive } = this.state.account;
    const { currentUser } = this.props;
    if (!accountExecutive && currentUser.isAdmin) {
      this.handleAccountExecutiveChange(currentUser.username);
    }

    const { activeJobQuantity, activeEmployeeQuantity, additionalOrgQuantity } =
      this.state.account.stripePriceIds || {};

    return (
      <UiDialog onClose={this.closeAddDialog} title={`New ${entityName}`}>
        <form>
          <UiDialog.Content>
            <Paper className="u-padding" variant="outlined">
              <div className="typo-label">Basic Information</div>
              <TextField
                label={`${entityName} Name`}
                className="u-width-100"
                value={org.name}
                onChange={this.onNameChange}
                margin="dense"
                required
              />
              <FormControl className="u-width-100">
                <InputLabel htmlFor="industryId">Industry *</InputLabel>
                <Select
                  value={org.industryId}
                  onChange={this.handleChange('industryId')}
                  className="u-width-100"
                  displayEmpty
                  inputProps={{
                    name: 'industryId',
                    id: 'industryId',
                  }}
                >
                  {industries
                    ?.sort(sortAlphabetically((i: Industry) => i.name, SORT_ORDER.ASC))
                    .map((industry) => (
                      <MenuItem key={industry.industryId} value={industry.industryId}>
                        {industry.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                label="Website (https://www.some-domain.com)"
                className="u-width-100 u-margin-right"
                value={org.website}
                onChange={this.handleChange('website')}
                error={org.website !== '' && !isValidHttpUrl(org.website)}
                required
              />
            </Paper>
            <Paper className="u-padding u-margin-top" variant="outlined">
              <div className="typo-label">{entityName} Image</div>
              <div className="u-flex-center">
                {!homePageImages ? (
                  <UiButton
                    className="u-width-100"
                    onClick={this.loadImages}
                    loading={isLoadingImages}
                    disabled={!org.website}
                  >
                    Scrape Homepage For Images
                  </UiButton>
                ) : null}
              </div>
              {homePageImages && homePageImages.length > 0 && !selectedImageUrl && file === null ? (
                <ImageGallery
                  minimumSize={MIN_IMAGE_SIZE}
                  onClick={this.selectImage}
                  images={homePageImages.map((imageUrl, index) => ({
                    id: index,
                    value: imageUrl,
                    imageUrl,
                    alt: `Downloaded from ${imageUrl}`,
                  }))}
                />
              ) : null}
              <FormControl margin="dense">
                <CustomerImageEditor
                  svgAllowed
                  onUpload={this.fileChange}
                  fixedWidth={150}
                  subtitle="Upload a Company Logo *"
                  selectedImageUrl={selectedImageUrl}
                  onUseNewImage={this.handleClearImage}
                />
                {selectedImageUrl ? (
                  <UiButton onClick={this.handleClearImage}>Select Different Image</UiButton>
                ) : null}
              </FormControl>
            </Paper>
            {isAddAccount ? (
              <>
                <Paper className="u-padding u-margin-top" variant="outlined">
                  <div className="typo-label">Subscription Information</div>
                  <ItemizedPlanSelectorBody
                    subscriptionCadence={
                      this.state.account.subscriptionCadence || SubscriptionCadence.MONTHLY
                    }
                    setSubscriptionCadence={this.handleChangeSubscriptionCadence}
                    activeJobCount={activeJobQuantity || null}
                    changeJobCount={this.handleChangeJobCount}
                    activeEmployeeCount={activeEmployeeQuantity || null}
                    changeEmployeeCount={this.handleChangeEmployeeCount}
                    additionalOrgCount={additionalOrgQuantity || null}
                    changeAdditionalOrgCount={this.handleChangeAdditionalOrgCount}
                  />
                  <div className="u-margin-top-sm u-flex-center">
                    <Checkbox
                      className="u-no-padding"
                      checked={account.isPartnerReferral}
                      onChange={this.handleIsPartnerReferralChange}
                    />
                    <div className="typo-title u-margin-left-md">
                      This is a partner referral (33% off for 3 months)
                    </div>
                  </div>
                </Paper>
                <AddAccountExecutive
                  handleChange={this.handleAccountExecutiveChange}
                  accountExecutiveUsername={accountExecutive}
                />
              </>
            ) : null}
            <Paper className="u-padding u-margin-top" variant="outlined">
              <div className="typo-label">Organization Location</div>
              {isAddAccount ? (
                <div className="typo-subtitle">
                  This is the location for the default organization we create
                </div>
              ) : null}
              <div className="u-flex-center">
                <TextField
                  label="City"
                  className="u-width-100 u-margin-right"
                  value={org.city}
                  onChange={this.handleChange('city')}
                  required
                />
                <div className="u-width-100 u-margin-x">
                  <StateAutocomplete value={org.state} onChange={this.handleStateChange} required />
                </div>
              </div>
            </Paper>
            {isAddAccount ? (
              <Paper className="u-padding u-margin-top" variant="outlined">
                <div className="typo-label">Business Information</div>
                <div className="typo-subtitle">
                  The physical location of the headquarters for the business, and a phone number for
                  it (their main phone line, in other words)
                </div>
                <div className="u-display-flex">
                  <TextField
                    className="u-width-100 u-margin-right"
                    label="Street"
                    value={account.businessStreet}
                    onChange={this.handleAccountChange('businessStreet')}
                    required
                  />
                  <TextField
                    className="u-width-100 u-margin-left"
                    label="Unit"
                    value={account.businessUnit}
                    onChange={this.handleAccountChange('businessUnit')}
                  />
                </div>
                <div className="u-flex-center">
                  <TextField
                    className="u-width-100 u-margin-right"
                    label="City"
                    value={account.businessCity}
                    onChange={this.handleAccountChange('businessCity')}
                    required
                  />
                  <div className="u-width-100 u-margin-x">
                    <StateAutocomplete
                      value={account.businessState}
                      onChange={this.handleAccountStateChange}
                      required
                    />
                  </div>
                  <TextField
                    className="u-width-100 u-margin-left"
                    label="Zip Code"
                    value={account.businessZipCode}
                    onChange={this.handleAccountChange('businessZipCode')}
                    error={Boolean(account.businessZipCode && !isZipCode(account.businessZipCode))}
                    required
                  />
                </div>
                <UiPhoneTextField
                  className="u-width-100"
                  label="Phone Number"
                  value={account.businessPhoneNumber}
                  onChange={this.handleAccountPhoneNumberChange}
                  onError={this.handleAccountPhoneNumberError}
                  required
                />
              </Paper>
            ) : null}
            <Paper className="u-padding u-margin-top" variant="outlined">
              <div className="typo-label">Indeed Setup</div>
              <TextField
                className="u-width-100"
                label="Indeed Account Email"
                value={org.indeedEmail}
                onChange={this.handleChange('indeedEmail')}
                margin="dense"
              />
              <TextField
                className="u-width-100"
                label="Indeed Credit Card Zip Code"
                value={org.indeedZipCode}
                onChange={this.handleChange('indeedZipCode')}
                margin="dense"
              />
              <FormControl margin="dense" className="u-width-100">
                <InputLabel htmlFor="industryId">Include Job Template</InputLabel>
                <Select
                  value={org.jobTemplate}
                  onChange={this.handleChange('jobTemplate')}
                  className="u-width-100"
                  displayEmpty
                >
                  {JOB_TEMPLATE_OPTIONS.map((template) => (
                    <MenuItem key={template.value} value={template.value}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
            <Paper className="u-padding u-margin-top" variant="outlined">
              <div className="typo-label">Request Specific Area Code for Phone Numbers</div>
              <div className="u-margin-top-sm u-flex-center">
                <UiAreaCodeTextField
                  id="areaCode"
                  label="Requested Area Code"
                  value={areaCode}
                  onChange={this.handleAreaCodeChange}
                  margin="dense"
                  isValid={isAreaCodeValid}
                  errorText={
                    isAreaCodeValid === false
                      ? 'This area code has no available phone numbers'
                      : null
                  }
                />
              </div>
            </Paper>
            <Paper className="u-padding u-margin-top" variant="outlined">
              <div className="u-margin-top-sm u-flex-center">
                <Checkbox
                  className="u-no-padding"
                  checked={org.internal}
                  onChange={this.handleInternalChange}
                />
                <div className="typo-title u-margin-left-md">
                  This is an internal Team Engine account, not used for a customer
                </div>
              </div>
              {org.internal ? (
                <div className="typo-subtitle">
                  This account will not post to job boards, or have its phone numbers registered
                  with carriers
                </div>
              ) : null}
            </Paper>
            {accountId && (
              <Paper className="u-padding u-margin-top" variant="outlined">
                <div className="typo-label">Grant Access to Users</div>
                <UsersPicker
                  accountId={accountId}
                  onSelectedUsersChange={this.handleGrantAccessChange}
                />
              </Paper>
            )}
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton onClick={this.closeAddDialog} variant="dismiss">
              Cancel
            </UiButton>
            <div>
              {isAddAccount && (
                <UiButton
                  className="u-margin-right"
                  onClick={this.handleAddWithUserClick}
                  variant="secondary"
                  disabled={submitDisabled}
                  loading={isCreating}
                >
                  Add With User
                </UiButton>
              )}
              <UiButton
                onClick={this.handleAddClick}
                disabled={submitDisabled}
                loading={isCreating}
              >
                Add
              </UiButton>
            </div>
          </UiDialog.Actions>
        </form>
      </UiDialog>
    );
  }

  render() {
    const { adding, addingUser, createdOrg } = this.state;

    return (
      <>
        <UiButton variant="secondary" onClick={this.openAddDialog}>
          Add {this.props.accountId === null ? 'Account' : 'Organization'}
        </UiButton>
        {adding ? this.renderDialog() : null}
        {addingUser && createdOrg ? (
          <UserForm accountId={createdOrg.accountId} onClose={this.handleCloseUserDialog} />
        ) : null}
      </>
    );
  }
}

export const AddOrganizationOrAccount = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddOrganizationOrAccountComponent);
